import { useState, useEffect, useRef } from 'react';
import { TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InputText } from './InputText';
import { useDecimalSeparator } from '@/utils/formatting';
import {
    formatNumberForInput,
    parseLocaleNumber,
    hasWrongDecimalSeparator
} from '@/utils/localeUtils';

export interface NumericInputProps
    extends Omit<TextFieldProps, 'value' | 'onChange' | 'type'> {
    /** Current numeric value. Pass `undefined` or `null` for empty. */
    value: number | null | undefined;
    /** Called with the parsed JS number (always dot-decimal) or null when empty/invalid. */
    onChange: (value: number | null) => void;
    /** Number of decimal digits to display. Defaults to 2. */
    digits?: number;
    /**
     * Override the detected decimal separator.
     * Detected automatically from company country when omitted.
     */
    decimalSeparator?: ',' | '.';
    inputAdornment?: React.ReactNode;
    spacing?: string;
    allowDisabledEdit?: boolean;
    onDisabledEditClick?: () => void;
    inputStyles?: React.CSSProperties;
    containerStyles?: React.CSSProperties;
    /** Called whenever the wrong-separator format error appears or clears. */
    onFormatError?: (hasError: boolean) => void;
}

/**
 * Locale-aware numeric input component.
 *
 * - Danish / European companies: accepts and displays `100,00` (comma decimal)
 * - English / US companies: accepts and displays `100.00` (dot decimal)
 *
 * `onChange` always emits a native JS `number`, so JSON serialization to the
 * backend always uses dot decimal — backend invariant is preserved.
 *
 * Uses `type="text"` + `inputMode="decimal"` so:
 *  - The browser never hijacks the decimal key on mobile
 *  - Both comma and dot are accepted as characters
 */
export const NumericInput = ({
    value,
    onChange,
    digits = 2,
    decimalSeparator: separatorProp,
    error: externalError,
    helperText: externalHelperText,
    onFormatError,
    ...rest
}: NumericInputProps) => {
    const { t } = useTranslation();
    const { decimalSeparator: detectedSeparator } = useDecimalSeparator();
    const separator = separatorProp ?? detectedSeparator;

    /**
     * Convert the external numeric value to the display string.
     * Integers with digits=0 are shown without a decimal part.
     */
    const toDisplay = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '';
        return formatNumberForInput(num, separator, digits);
    };

    const [displayValue, setDisplayValue] = useState<string>(() =>
        toDisplay(value)
    );

    // Wrong separator detection for requirement: "show error when wrong format"
    const [wrongSeparator, setWrongSeparator] = useState(false);

    // Decimal not allowed when digits === 0
    const [decimalNotAllowed, setDecimalNotAllowed] = useState(false);

    // Track whether the input is focused so we don't reformat mid-typing
    const isFocused = useRef(false);

    // Sync external value → display string (only when blurred)
    useEffect(() => {
        if (!isFocused.current) {
            setDisplayValue(toDisplay(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, separator, digits]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setDisplayValue(raw);
        const isWrong = hasWrongDecimalSeparator(raw, separator);
        setWrongSeparator(isWrong);

        // When digits=0, reject any decimal separator in the input
        const hasDecimal = raw.includes(',') || raw.includes('.');
        const noDecimalsError = digits === 0 && hasDecimal;
        setDecimalNotAllowed(noDecimalsError);

        onFormatError?.(isWrong || noDecimalsError);

        // Allow typing a leading minus or trailing separator without emitting NaN
        if (
            raw === '' ||
            raw === '-' ||
            raw === separator ||
            raw === `-${separator}`
        ) {
            onChange(raw === '' ? null : null);
            return;
        }

        // Wrong separator or decimals not allowed: emit null so parents treat the value as invalid
        if (isWrong || noDecimalsError) {
            onChange(null);
            return;
        }

        const parsed = parseLocaleNumber(raw, separator);
        onChange(parsed);
    };

    const handleBlur = () => {
        isFocused.current = false;
        // Reformat the display value on blur (normalize trailing separator, extra zeros, etc.)
        const parsed = parseLocaleNumber(displayValue, separator);
        setDisplayValue(parsed !== null ? toDisplay(parsed) : '');
        setWrongSeparator(false);
        setDecimalNotAllowed(false);
        onFormatError?.(false);
    };

    const handleFocus = () => {
        isFocused.current = true;
    };

    const formatError = decimalNotAllowed
        ? t('numeric_input.no_decimals_allowed')
        : wrongSeparator
        ? separator === ','
            ? t('numeric_input.use_comma_decimal')
            : t('numeric_input.use_dot_decimal')
        : undefined;

    return (
        <InputText
            {...rest}
            type="text"
            inputProps={{
                inputMode: 'decimal',
                ...(rest.inputProps ?? {})
            }}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            error={externalError || wrongSeparator || decimalNotAllowed}
            helperText={formatError ?? externalHelperText}
        />
    );
};
